import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { trackLink } from '@fs/zion-analytics'
import { ContentAdd, ContentCheck } from '@fs/zion-icon'
import { useUser } from '@fs/zion-user'
import { useStatusOverlay } from '@fs/zion-ui'
import { useFlagsContext, useLoggedOutOverlay, usePlaylistContainerContext } from '../../providers'
import { FLAGS } from '../../config'
import { usePlaylists } from '../../hooks/playlist/usePlaylists'
import { usePlaylistCreation } from '../../hooks/playlist/usePlaylistCreation'
import { usePlaylistDeletion } from '../../hooks/playlist/usePlaylistDeletion'
import ChewableButton from './ChewableButton'
import notifySentry from '../helpers/sentryNotifier'

const EMPHASIS = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  SPECIAL: 'special',
}

export default function FollowSeriesButton({ seriesId, asIconButton, size, ...props }) {
  const [t] = useTranslation()
  const user = useUser()
  const { [FLAGS.NEW_PLAYLIST_STATE]: newPlaylistStateFlag } = useFlagsContext()

  // Old provider-based state (deprecated)
  const oldContext = usePlaylistContainerContext()
  const {
    followPlaylist: oldFollowPlaylist,
    deletePlaylist: oldDeletePlaylist,
    isFollowing: oldIsFollowing,
    playlists: oldPlaylists,
  } = oldContext || {}

  // New React Query-based state
  const { playlists = [] } = usePlaylists()
  const { createPlaylist: createPlaylistMutation } = usePlaylistCreation()
  const { deletePlaylist: deletePlaylistMutation } = usePlaylistDeletion()

  const showStatusOverlay = useStatusOverlay()
  const { overlay: loggedOutOverlay } = useLoggedOutOverlay({
    addToPlaylist: seriesId,
    playlistItemType: 'session',
  })
  const [following, setFollowing] = useState(false)

  useEffect(() => {
    if (newPlaylistStateFlag.isOn) {
      // New approach: check if any playlist has this series as parent
      setFollowing(playlists.some((p) => p.parent?.id === seriesId))
    } else if (oldPlaylists) {
      // Old approach
      setFollowing(oldIsFollowing(seriesId))
    }
  }, [newPlaylistStateFlag.isOn, playlists, oldPlaylists, oldIsFollowing, seriesId])

  const onClick = useCallback(
    async (e) => {
      const loggedIn = user?.signedIn ?? false
      if (!loggedIn) {
        trackLink({ name: 'Playlists: logged out', action: 'click_action' })
        loggedOutOverlay?.handleClick?.(e)
      } else if (!following) {
        trackLink({ name: 'Playlists: logged in', action: 'follow_series' })
        try {
          if (newPlaylistStateFlag.isOn) {
            // New React Query approach - use createPlaylist hook with seriesId
            await createPlaylistMutation({ seriesId })
            setFollowing(true)
          } else {
            // Old context approach
            await oldFollowPlaylist(seriesId)
            setFollowing(true)
          }
        } catch (err) {
          showStatusOverlay({
            message: t(
              'playlists.errors.generic.follow',
              "We're sorry, but there was an issue following this series. Please try again shortly while we investigate."
            ),
            type: 'error',
            dismissable: true,
          })
          notifySentry({ ...err.response, err })
        }
      } else {
        trackLink({ name: 'Playlists: logged in', action: 'unfollow_series' })
        if (newPlaylistStateFlag.isOn) {
          // New React Query approach
          const followedPlaylist = playlists.find((p) => p.parent?.id === seriesId)
          if (followedPlaylist) {
            await deletePlaylistMutation(followedPlaylist.id)
            setFollowing(false)
          }
        } else {
          // Old context approach
          const { id } = [...oldPlaylists.values()].find((p) => p.parent?.id === seriesId)
          await oldDeletePlaylist(id)
          setFollowing(false)
        }
      }
    },
    [
      oldDeletePlaylist,
      oldFollowPlaylist,
      createPlaylistMutation,
      deletePlaylistMutation,
      following,
      loggedOutOverlay,
      newPlaylistStateFlag.isOn,
      playlists,
      oldPlaylists,
      seriesId,
      showStatusOverlay,
      t,
      user?.signedIn,
    ]
  )

  const label = following
    ? t('playlists.series.following.label', 'Following')
    : t('playlists.series.follow.label', 'Follow')

  return (
    <ChewableButton
      asIconButton={asIconButton}
      size={size}
      onClick={onClick}
      Icon={following ? ContentCheck : ContentAdd}
      emphasis={EMPHASIS.LOW}
      data-testid="playlists-series"
      {...props}
    >
      {label}
    </ChewableButton>
  )
}
